import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledDropdown } from 'reactstrap';
import {
  GED_DOCUMENT_DELETE,
  GED_DOCUMENT_DOWNLOAD,
  GED_DOCUMENT_LIST,
  GED_DOCUMENT_RESTORE,
  GED_DOCUMENT_TRASH,
  GED_DOCUMENT_VALIDATE_BATCH,
  PLAN_FOLDERS,
  PLAN_META_LIST,
} from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import { SimpleSpinner } from '../../../ui/Spinner';
import './ArchiveStyles.css';
import { getAssignedServiceIds } from './serviceScope';

const attachParent = (nodes, parentId = null) =>
  (nodes || []).map((node) => ({
    ...node,
    parentId,
    children: node.children ? attachParent(node.children, node.id) : [],
  }));

const findPath = (nodes, targetId, path = []) => {
  for (const node of nodes) {
    const nextPath = [...path, node];
    if (node.id === targetId) return nextPath;
    if (node.children && node.children.length > 0) {
      const result = findPath(node.children, targetId, nextPath);
      if (result.length > 0) return result;
    }
  }
  return [];
};

const sortPlans = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  if (codeA && codeB && codeA !== codeB) {
    return codeA.localeCompare(codeB);
  }
  return (a.title || '').toString().localeCompare((b.title || '').toString());
};

const buildPlanIndex = (plans) => {
  const index = new Map();
  (plans || []).forEach((plan) => {
    const parentKey = plan.parentId ? Number(plan.parentId) : null;
    if (!index.has(parentKey)) {
      index.set(parentKey, []);
    }
    index.get(parentKey).push(plan);
  });
  return index;
};

const buildPlanTree = (plans) => {
  const byId = new Map();
  (plans || []).forEach((plan) => {
    byId.set(Number(plan.id), { ...plan, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const parentId = node.parentId ? Number(node.parentId) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNode = (node) => {
    node.children.sort(sortPlans);
    node.children.forEach(sortNode);
  };
  roots.sort(sortPlans);
  roots.forEach(sortNode);
  return roots;
};

const flattenPlanTree = (nodes, expandedIds, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({ plan: node, depth });
    if (node.children?.length > 0 && expandedIds.has(Number(node.id))) {
      acc.push(...flattenPlanTree(node.children, expandedIds, depth + 1));
    }
    return acc;
  }, []);

const collectPlanDescendants = (plans, rootId) => {
  const startId = Number(rootId);
  if (!startId) return [];
  const index = buildPlanIndex(plans);
  const stack = [startId];
  const ids = new Set();
  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId || ids.has(currentId)) continue;
    ids.add(currentId);
    const children = index.get(currentId) || [];
    children.forEach((child) => {
      stack.push(Number(child.id));
    });
  }
  return Array.from(ids);
};

const trashReasonOptions = [
  { value: 'Erreur de classement', label: 'Erreur de classement' },
  { value: 'Cycle de vie atteint', label: 'Cycle de vie atteint' },
  { value: 'Doublon', label: 'Doublon' },
  { value: 'Autre', label: 'Autre (préciser)' },
];

const deleteReasonOptions = [
  { value: 'Validation archiviste', label: 'Validation archiviste' },
  { value: 'Conformité règles de conservation', label: 'Conformité règles de conservation' },
  { value: 'Autre', label: 'Autre (préciser)' },
];

const restoreReasonOptions = [
  { value: 'Restauration', label: 'Restauration' },
  { value: 'Erreur de suppression', label: 'Erreur de suppression' },
  { value: 'Autre', label: 'Autre (préciser)' },
];

const Archives = () => {
  const session = useSelector((state) => state.sessionAuth);
  const profile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const location = useLocation();

  const [plans, setPlans] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [searchScope, setSearchScope] = useState('all');
  const [quickFilter, setQuickFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [showTrash, setShowTrash] = useState(false);
  const [validationFilter, setValidationFilter] = useState('');
  const [importBatchFilter, setImportBatchFilter] = useState(() => new URLSearchParams(location.search).get('batchId') || '');
  const [trashModalOpen, setTrashModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [validateWholeBatch, setValidateWholeBatch] = useState(false);
  const [batchOutcome, setBatchOutcome] = useState(null);
  const [trashReason, setTrashReason] = useState('');
  const [trashComment, setTrashComment] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteComment, setDeleteComment] = useState('');
  const [restoreReason, setRestoreReason] = useState('');
  const [restoreComment, setRestoreComment] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const [expandedPlanIds, setExpandedPlanIds] = useState(new Set());
  const planSelectRef = useRef(null);

  const roles = Array.isArray(session.roles) ? session.roles : [];
  const accessFilters = profile?.accessFilters || {};
  const isAssistantOnly =
    roles.includes('ROLE_ASSIST')
    && !roles.some((role) => ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role));
  const isReaderOnly =
    roles.includes('ROLE_USER')
    && !roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
    );
  const applyServiceScope = useMemo(
    () => isAssistantOnly || isReaderOnly,
    [isAssistantOnly, isReaderOnly]
  );
  const planScopeIds = useMemo(
    () => (Array.isArray(accessFilters.planIds) ? accessFilters.planIds.map(Number) : []),
    [accessFilters.planIds]
  );
  const folderScopeIds = useMemo(
    () => (Array.isArray(accessFilters.folderIds) ? accessFilters.folderIds.map(Number) : []),
    [accessFilters.folderIds]
  );
  const maxConfidentialite = accessFilters.maxConfidentialite || '';
  const allowExport = accessFilters.allowExport !== false;
  const serviceScopeIds = useMemo(() => getAssignedServiceIds(profile), [profile]);
  const hasPlanScope = planScopeIds.length > 0;
  const hasFolderScope = folderScopeIds.length > 0;
  const hasServiceData = useMemo(() => {
    const stack = [...tree];
    while (stack.length > 0) {
      const node = stack.pop();
      if (node?.serviceUnit?.id) {
        return true;
      }
      if (node?.children?.length) {
        stack.push(...node.children);
      }
    }
    return false;
  }, [tree]);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setLoading(true);
      try {
        const [plansRes, foldersRes] = await Promise.all([
          axios.get(PLAN_META_LIST(), authHeaders),
          axios.get(PLAN_FOLDERS(), authHeaders),
        ]);
        if (!isMounted) return;
        const planList = plansRes.data?.plans || [];
        const folderTree = attachParent(foldersRes.data?.folders || []);
        setPlans(planList);
        setTree(folderTree);
      } catch (err) {
        ToastError('Impossible de charger les plans ou dossiers');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      isMounted = false;
    };
  }, [authHeaders]);

  const matchesPlanScope = (planId) =>
    !hasPlanScope || planScopeIds.includes(Number(planId));

  const matchesServiceScope = (node) => {
    if (!applyServiceScope || !serviceScopeIds.length || !hasServiceData) return true;
    const service = node.serviceUnit;
    if (!service || !service.id) return false;
    return serviceScopeIds.includes(Number(service.id));
  };

  const matchesFolderScope = (folderId) =>
    !hasFolderScope || folderScopeIds.includes(Number(folderId));

  const scopedTree = useMemo(() => {
    const filterNodes = (nodes) =>
      (nodes || []).reduce((acc, node) => {
        if (!matchesPlanScope(node.planId)) {
          return acc;
        }
        const children = node.children ? filterNodes(node.children) : [];
        const serviceOk = matchesServiceScope(node);
        const folderOk = matchesFolderScope(node.id);
        const include =
          (serviceOk && (!hasFolderScope || folderOk)) || children.length > 0;
        if (!include) {
          return acc;
        }
        acc.push({ ...node, children });
        return acc;
      }, []);

    return filterNodes(tree);
  }, [tree, hasFolderScope, folderScopeIds, hasPlanScope, planScopeIds, serviceScopeIds, hasServiceData, applyServiceScope]);

  const availablePlans = useMemo(() => {
    let list = plans;
    if (hasPlanScope) {
      list = list.filter((plan) => planScopeIds.includes(Number(plan.id)));
    }
    if ((applyServiceScope && serviceScopeIds.length) || hasFolderScope) {
      const allowedPlanIds = new Set();
      const visit = (nodes) => {
        nodes.forEach((node) => {
          if (!matchesPlanScope(node.planId)) return;
          const serviceOk = matchesServiceScope(node);
          const folderOk = matchesFolderScope(node.id);
          if (serviceOk && folderOk && node.planId) {
            allowedPlanIds.add(Number(node.planId));
          }
          if (node.children && node.children.length > 0) {
            visit(node.children);
          }
        });
      };
      visit(tree);
      if (allowedPlanIds.size > 0) {
        list = list.filter((plan) => allowedPlanIds.has(Number(plan.id)));
      } else {
        list = [];
      }
    }
    return list;
  }, [
    plans,
    tree,
    hasPlanScope,
    planScopeIds,
    hasFolderScope,
    folderScopeIds,
    serviceScopeIds,
    hasServiceData,
    applyServiceScope,
  ]);

  const planIndex = useMemo(() => buildPlanIndex(availablePlans), [availablePlans]);

  const selectedPlanIds = useMemo(
    () => collectPlanDescendants(availablePlans, selectedPlanId),
    [availablePlans, selectedPlanId]
  );

  const selectedPlanChildren = useMemo(() => {
    if (!selectedPlanId) return [];
    const children = planIndex.get(Number(selectedPlanId)) || [];
    return [...children].sort(sortPlans);
  }, [planIndex, selectedPlanId]);

  const planTree = useMemo(() => buildPlanTree(availablePlans), [availablePlans]);
  const groupedPlanOptions = useMemo(
    () => flattenPlanTree(planTree, expandedPlanIds),
    [planTree, expandedPlanIds]
  );

  const togglePlanMenu = () => {
    if (!planMenuOpen) {
      const openIds = new Set(planTree.map((plan) => Number(plan.id)));
      const plansById = new Map(availablePlans.map((plan) => [Number(plan.id), plan]));
      let current = selectedPlan;
      while (current?.parentId) {
        openIds.add(Number(current.parentId));
        current = plansById.get(Number(current.parentId));
      }
      setExpandedPlanIds(openIds);
    }
    setPlanMenuOpen((open) => !open);
  };

  const togglePlanBranch = (id) => {
    setExpandedPlanIds((previous) => {
      const next = new Set(previous);
      if (next.has(Number(id))) next.delete(Number(id));
      else next.add(Number(id));
      return next;
    });
  };

  useEffect(() => {
    const closePlanMenu = (event) => {
      if (!planSelectRef.current?.contains(event.target)) {
        setPlanMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closePlanMenu);
    return () => document.removeEventListener('mousedown', closePlanMenu);
  }, []);

  useEffect(() => {
    if (availablePlans.length === 0) {
      setSelectedPlanId('');
      return;
    }
    const allowed = availablePlans.some(
      (plan) => Number(plan.id) === Number(selectedPlanId)
    );
    if (!selectedPlanId || !allowed) {
      setSelectedPlanId(availablePlans[0].id);
    }
  }, [availablePlans, selectedPlanId]);

  const filteredTree = useMemo(() => {
    if (!selectedPlanId) return [];
    return scopedTree.filter(
      (node) => Number(node.planId) === Number(selectedPlanId)
    );
  }, [scopedTree, selectedPlanId]);

  useEffect(() => {
    if (!selectedPlanId) {
      setSelectedFolderId('');
      setExpandedNodes(new Set());
      return;
    }
    setSelectedFolderId('');
    setExpandedNodes(new Set());
  }, [selectedPlanId, filteredTree]);

  const selectedPlan = useMemo(
    () => availablePlans.find((plan) => Number(plan.id) === Number(selectedPlanId)),
    [availablePlans, selectedPlanId]
  );

  const selectedPath = useMemo(() => {
    if (!selectedFolderId) return [];
    return findPath(tree, Number(selectedFolderId));
  }, [tree, selectedFolderId]);
  const parentFolder = selectedPath.length > 1 ? selectedPath[selectedPath.length - 2] : null;
  const parentPlan = selectedPlan?.parentId
    ? availablePlans.find((plan) => Number(plan.id) === Number(selectedPlan.parentId))
    : null;
  const canGoUp = Boolean(selectedFolderId || parentPlan);
  const upLabel = selectedFolderId
    ? parentFolder ? `Dossier parent · ${parentFolder.title}` : `Remonter au plan · ${selectedPlan?.title || ''}`
    : parentPlan ? `Remonter · ${parentPlan.title}` : 'Premier niveau du plan';
  const goUp = () => {
    if (selectedFolderId) {
      setSelectedFolderId(parentFolder?.id || '');
    } else if (parentPlan) {
      setSelectedPlanId(parentPlan.id);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [selectedPlanId, selectedFolderId, searchTerm, searchScope, quickFilter, sortBy, perPage, showTrash, validationFilter, importBatchFilter]);

  useEffect(() => {
    setImportBatchFilter(new URLSearchParams(location.search).get('batchId') || '');
  }, [location.search]);

  useEffect(() => {
    setSelectedIds([]);
  }, [selectedPlanId, selectedFolderId, searchTerm, quickFilter, showTrash, validationFilter, importBatchFilter, page, perPage]);

  useEffect(() => {
    if (!importBatchFilter && !selectedPlanId && availablePlans.length === 0) {
      setDocuments([]);
      setDocsLoading(false);
      setTotalCount(0);
      return undefined;
    }
    let active = true;
    const handler = setTimeout(() => {
      setDocsLoading(true);
          axios
        .get(GED_DOCUMENT_LIST(), {
          ...authHeaders,
          params: {
            planIds: !importBatchFilter && selectedPlanIds.length > 0 ? selectedPlanIds : undefined,
            folderId: !importBatchFilter && selectedFolderId ? selectedFolderId : undefined,
            search: searchTerm || undefined,
            scope: searchScope || undefined,
            filter: quickFilter || undefined,
            sort: sortBy || undefined,
            status: showTrash ? 'En corbeille' : (validationFilter || undefined),
            batchId: importBatchFilter || undefined,
            page,
            perPage,
          },
        })
        .then((res) => {
          if (!active) return;
          const items = res.data?.documents || [];
          const mapped = items.map((doc) => ({
            id: doc.id,
            uuid: doc.uuid,
            title: doc.title,
            status: doc.status,
            validationStatus: doc.validationStatus || doc.status,
            profile: doc.archiveProfile || null,
            regle: doc.regleConservation || null,
            service: doc.producerService || null,
            type: doc.archiveProfile?.documentType || doc.archiveProfile?.title || doc.type,
            code: doc.reference || doc.code,
            date: doc.date || doc.createdAt,
            confidentialite: doc.archiveProfile?.confidentialite || doc.confidentialite,
            folderId: doc.folderId || doc.folder?.id,
            planId: doc.planId || doc.folder?.planId,
            versionCount: doc.versionCount || 0,
            hasFile: Boolean(doc.hasFile),
          }));
          setDocuments(mapped);
          setTotalCount(res.data?.count ?? mapped.length);
        })
        .catch(() => {
          if (!active) return;
          ToastError("Impossible de charger les archives");
          setDocuments([]);
          setTotalCount(0);
        })
        .finally(() => {
          if (active) setDocsLoading(false);
        });
    }, 350);

    return () => {
      active = false;
      clearTimeout(handler);
    };
  }, [
    authHeaders,
    selectedPlanId,
    selectedPlanIds,
    availablePlans.length,
    selectedFolderId,
    searchTerm,
    searchScope,
    quickFilter,
    sortBy,
    page,
    perPage,
    showTrash,
    validationFilter,
    importBatchFilter,
    refreshKey,
  ]);

  useEffect(() => {
    setSelectedIds([]);
  }, [documents]);

  useEffect(() => {
    setSelectedIds([]);
  }, [showTrash]);

  const filteredDocs = useMemo(() => {
    if (showTrash) {
      return documents.filter((doc) => doc.status === 'En corbeille');
    }
    return documents.filter(
      (doc) => doc.status !== 'En corbeille' && doc.status !== 'Détruite'
    );
  }, [documents, showTrash]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / perPage)),
    [totalCount, perPage]
  );
  const pageStart = totalCount === 0 ? 0 : (page - 1) * perPage + 1;
  const pageEnd = Math.min(page * perPage, totalCount);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const isAllSelected =
    filteredDocs.length > 0 && selectedIds.length === filteredDocs.length;

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(filteredDocs.map((doc) => doc.id));
  };

  const toggleSelectItem = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleNode = (node, siblings) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      const closeBranch = (branch) => {
        next.delete(branch.id);
        (branch.children || []).forEach(closeBranch);
      };
      if (next.has(node.id)) {
        closeBranch(node);
      } else {
        const activePathIds = new Set(selectedPath.map((item) => Number(item.id)));
        siblings
          .filter((sibling) => Number(sibling.id) !== Number(node.id) && !activePathIds.has(Number(sibling.id)))
          .forEach(closeBranch);
        next.add(node.id);
      }
      return next;
    });
  };

  const selectFolder = (node) => {
    const path = findPath(tree, Number(node.id));
    setExpandedNodes(new Set(path.map((item) => item.id)));
    setSelectedFolderId(node.id);
  };

  const planLabel = selectedPlan
    ? `${selectedPlan.code} - ${selectedPlan.title}`
    : 'Sélectionnez un plan';

  const pathLabel = selectedPath.length
    ? selectedPath.map((node) => node.title).join(' / ')
    : 'Racine';

  const canCreateArchive = useMemo(() => {
    const canRole = roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
    );
    if (!canRole) return false;
    if (!selectedPlanId) return false;
    if (roles.includes('ROLE_ASSIST') && hasPlanScope) {
      return planScopeIds.includes(Number(selectedPlanId));
    }
    return true;
  }, [roles, selectedPlanId, hasPlanScope, planScopeIds]);

  const canImportArchive = useMemo(
    () =>
      roles.some((role) =>
        ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
      ),
    [roles]
  );

  const canViewDocuments = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST', 'ROLE_USER'].includes(role)
  );
  const canDownloadDocuments = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  ) && allowExport;
  const canEditDocuments = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  );
  const canTrashDocuments = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
  );
  const canDeleteDocuments = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
  );
  const canValidateDocuments = canDeleteDocuments;

  const handleCreateArchive = () => {
    if (!canCreateArchive) return;
    const planId = selectedPlanId || (availablePlans[0]?.id ?? '');
    const folderId = selectedFolderId || '';
    const params = new URLSearchParams();
    if (planId) params.set('planId', planId);
    if (folderId) params.set('folderId', folderId);
    const query = params.toString();
    navigate(`/apps/in/archives/add${query ? `?${query}` : ''}`);
  };

  const handleImportArchive = () => {
    if (!canImportArchive) return;
    const planId = selectedPlanId || (availablePlans[0]?.id ?? '');
    const folderId = selectedFolderId || '';
    const params = new URLSearchParams();
    if (planId) params.set('planId', planId);
    if (folderId) params.set('folderId', folderId);
    const query = params.toString();
    navigate(`/apps/in/archives/import${query ? `?${query}` : ''}`);
  };


  const handleAdvancedSearch = () => {
    const params = new URLSearchParams();
    if (selectedPlanId) params.set('planId', selectedPlanId);
    if (selectedFolderId) params.set('folderId', selectedFolderId);
    const query = params.toString();
    navigate(`/apps/in/archives/search${query ? `?${query}` : ''}`);
  };

  const parseFilename = (headers, fallback) => {
    const disposition = headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="(.+?)"/);
    if (match && match[1]) {
      return match[1];
    }
    return fallback || 'archive';
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  };

  const handleView = (doc) => {
    if (!canViewDocuments) return;
    if (!doc?.uuid) {
      ToastError('UUID introuvable pour ce document.');
      return;
    }
    navigate(`/apps/in/archives/detail/${doc.uuid}`);
  };

  const handleDownload = async (doc) => {
    if (!canDownloadDocuments || !doc.hasFile) return;
    try {
      const res = await axios.get(GED_DOCUMENT_DOWNLOAD(doc.id), {
        ...authHeaders,
        responseType: 'blob',
      });
      const filename = parseFilename(res.headers, `${doc.title || 'archive'}`);
      downloadBlob(res.data, filename);
    } catch (err) {
      ToastError('Impossible de télécharger le document');
    }
  };

  const handleEdit = (doc) => {
    if (!canEditDocuments) return;
    if (!doc?.uuid) {
      ToastError('UUID introuvable pour ce document.');
      return;
    }
    navigate(`/apps/in/archives/add?docUuid=${doc.uuid}`);
  };

  const openTrashModal = () => {
    if (!canTrashDocuments || selectedIds.length === 0) return;
    setTrashModalOpen(true);
  };

  const submitBatchValidation = async () => {
    if (!canValidateDocuments || actionLoading || (validateWholeBatch ? !importBatchFilter : selectedIds.length === 0)) return;
    setActionLoading(true);
    try {
      const response = await axios.post(GED_DOCUMENT_VALIDATE_BATCH(), validateWholeBatch
        ? { batchId: importBatchFilter }
        : { ids: selectedIds, batchId: importBatchFilter || undefined }, authHeaders);
      const payload = response.data || {};
      const validatedCount = payload.validated?.length || 0;
      setBatchOutcome(payload);
      setBatchModalOpen(false);
      setValidateWholeBatch(false);
      setSelectedIds([]);
      setRefreshKey((previous) => previous + 1);
      ToastConfirm(`${validatedCount} archive${validatedCount > 1 ? 's' : ''} validée${validatedCount > 1 ? 's' : ''} en lot.`);
    } catch (error) {
      ToastError(error.response?.data?.message || 'Validation du lot impossible.');
    } finally {
      setActionLoading(false);
    }
  };

  const closeTrashModal = () => {
    setTrashModalOpen(false);
    setTrashReason('');
    setTrashComment('');
  };

  const openDeleteModal = () => {
    if (!canDeleteDocuments || selectedIds.length === 0) return;
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteReason('');
    setDeleteComment('');
    setDeleteConfirmText('');
  };

  const openRestoreModal = () => {
    if (!canDeleteDocuments || selectedIds.length === 0) return;
    setRestoreModalOpen(true);
  };

  const closeRestoreModal = () => {
    setRestoreModalOpen(false);
    setRestoreReason('');
    setRestoreComment('');
  };

  const submitTrash = async () => {
    if (actionLoading) return;
    const commentRequired = trashReason === 'Autre';
    if (!trashReason) {
      ToastError('Motif requis.');
      return;
    }
    if (commentRequired && !trashComment.trim()) {
      ToastError('Commentaire requis pour le motif Autre.');
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        GED_DOCUMENT_TRASH(),
        { ids: selectedIds, reason: trashReason, comment: trashComment.trim() },
        authHeaders
      );
      const processed = res.data?.processed?.length ?? 0;
      ToastConfirm(`${processed} archive${processed > 1 ? 's' : ''} envoyée${processed > 1 ? 's' : ''} en corbeille.`);
      closeTrashModal();
      setSelectedIds([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      ToastError(err.response?.data?.message || "Impossible d'envoyer en elimination");
    } finally {
      setActionLoading(false);
    }
  };

  const submitDelete = async () => {
    if (actionLoading) return;
    const commentRequired = deleteReason === 'Autre';
    if (!deleteReason) {
      ToastError('Motif requis.');
      return;
    }
    if (commentRequired && !deleteComment.trim()) {
      ToastError('Commentaire requis pour le motif Autre.');
      return;
    }
    if (deleteConfirmText.trim() !== 'SUPPRIMER') {
      ToastError("Merci de saisir SUPPRIMER pour confirmer.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        GED_DOCUMENT_DELETE(),
        { ids: selectedIds, reason: deleteReason, comment: deleteComment.trim() },
        authHeaders
      );
      const processed = res.data?.processed?.length ?? 0;
      ToastConfirm(`${processed} archive${processed > 1 ? 's' : ''} supprimée${processed > 1 ? 's' : ''}.`);
      closeDeleteModal();
      setSelectedIds([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      ToastError(err.response?.data?.message || 'Suppression definitive impossible');
    } finally {
      setActionLoading(false);
    }
  };

  const submitRestore = async () => {
    if (actionLoading) return;
    const commentRequired = restoreReason === 'Autre';
    if (!restoreReason) {
      ToastError('Motif requis.');
      return;
    }
    if (commentRequired && !restoreComment.trim()) {
      ToastError('Commentaire requis pour le motif Autre.');
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(
        GED_DOCUMENT_RESTORE(),
        { ids: selectedIds, reason: restoreReason, comment: restoreComment.trim() },
        authHeaders
      );
      const processed = res.data?.processed?.length ?? 0;
      ToastConfirm(`${processed} archive${processed > 1 ? 's' : ''} restaurée${processed > 1 ? 's' : ''}.`);
      closeRestoreModal();
      setSelectedIds([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      ToastError(err.response?.data?.message || 'Restauration impossible');
    } finally {
      setActionLoading(false);
    }
  };

  const renderNode = (node, siblings) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isActive = Number(selectedFolderId) === Number(node.id);
    const isInPath = selectedPath.some((item) => Number(item.id) === Number(node.id));
    return (
      <li key={node.id}>
        <div className={`archive-tree-item ${isActive ? 'active' : ''} ${isInPath ? 'in-path' : ''}`}>
          <button
            type="button"
            className="archive-tree-toggle"
            onClick={() => toggleNode(node, siblings)}
            disabled={!hasChildren}
            aria-label={`${isExpanded ? 'Réduire' : 'Développer'} ${node.title}`}
            aria-expanded={hasChildren ? isExpanded : undefined}
          >
            <i
              className={
                hasChildren
                  ? isExpanded
                    ? 'ri-arrow-down-s-line'
                    : 'ri-arrow-right-s-line'
                  : ''
              }
            ></i>
          </button>
          <button type="button" className="archive-tree-select" aria-current={isActive ? 'location' : undefined} title={`${node.reference ? `${node.reference} - ` : ''}${node.title}`} onClick={() => selectFolder(node)}>
            <i className={isActive ? 'ri-folder-open-line' : 'ri-folder-2-line'} aria-hidden="true"></i>
            <span className="archive-tree-label">{node.reference && <strong>{node.reference} — </strong>}{node.title}</span>
          </button>
        </div>
        {hasChildren && isExpanded && (
          <ul className="archive-tree-children">
            {node.children.map((child) => renderNode(child, node.children))}
          </ul>
        )}
      </li>
    );
  };

  const confidentialityTone = (value) => {
    const normalized = (value || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (normalized.includes('tres') && normalized.includes('confidentiel')) return 'tone-critical';
    if (normalized.includes('confidentiel')) return 'tone-danger';
    if (normalized.includes('restreint')) return 'tone-restricted';
    return 'tone-info';
  };

  const downloadUnavailableReason = (doc) => {
    if (doc.status === 'En corbeille') return 'Téléchargement indisponible : archive dans la corbeille';
    if (!doc.hasFile) return 'Téléchargement indisponible : aucun fichier associé';
    return '';
  };

  const trashCommentRequired = trashReason === 'Autre';
  const deleteCommentRequired = deleteReason === 'Autre';
  const restoreCommentRequired = restoreReason === 'Autre';
  const canSubmitTrash = !!trashReason && (!trashCommentRequired || trashComment.trim() !== '');
  const canSubmitDelete =
    !!deleteReason
    && (!deleteCommentRequired || deleteComment.trim() !== '')
    && deleteConfirmText.trim() === 'SUPPRIMER';
  const canSubmitRestore =
    !!restoreReason && (!restoreCommentRequired || restoreComment.trim() !== '');

  return (
    <Fragment>
      <Breadcrumb title ="Archives" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-8 col-lg-9 col-xl-9">
              <div className="archive-page-header">
                <div>
                  <h4 className="htitle">Accueil archives</h4>
                  <p className="text-muted mb-0">Accès rapide aux plans et dossiers autorisés.</p>
                </div>
                <div className="archive-action-buttons">
                  <button
                    className="btn btn-outline-primary btn-file-action"
                    type="button"
                    onClick={handleImportArchive}
                    disabled={!canImportArchive}
                  >
                    <i className="ri-upload-2-line me-1"></i> Importer un ZIP
                  </button>
                  <button
                    className="btn btn-success btn-uniform-action"
                    type="button"
                    onClick={handleCreateArchive}
                    disabled={!canCreateArchive}
                  >
                    <i className="ri-add-line me-1"></i> Nouvelle archive
                  </button>
                  {canImportArchive && <Link
                    className="btn btn-outline-primary btn-uniform-action"
                    to="/apps/in/archives/taches-incompletes"
                  >
                    <i className="ri-task-line" aria-hidden="true"></i> Tâches incomplètes
                  </Link>}
                </div>
              </div>

              <div className="archive-explorer">
                <div className={`archive-panel ${planMenuOpen ? 'archive-plan-menu-open' : ''}`}>
                  <div className="archive-navigation-heading">Plan de classement</div>
                  <div className="archive-panel-header">
                    <div className="archive-plan-select" ref={planSelectRef}>
                      <button
                        type="button"
                        className="archive-plan-trigger"
                        onClick={togglePlanMenu}
                        disabled={availablePlans.length === 0}
                        aria-expanded={planMenuOpen}
                        aria-haspopup="true"
                        aria-controls="archive-plan-options"
                        title={selectedPlan ? `${selectedPlan.code} - ${selectedPlan.title}` : undefined}
                      >
                        <i className="ri-node-tree archive-plan-trigger-icon" aria-hidden="true"></i>
                        <span className="archive-plan-trigger-label">
                          {selectedPlan
                            ? `${selectedPlan.code} - ${selectedPlan.title}`
                            : availablePlans.length === 0
                              ? 'Aucun plan autorisé'
                              : 'Choisir un plan'}
                        </span>
                        <span className="archive-plan-trigger-actions">
                          {selectedPlan && (
                            <i className="ri-checkbox-circle-line" aria-label="Plan sélectionné"></i>
                          )}
                          <i className="ri-arrow-down-s-line" aria-hidden="true"></i>
                        </span>
                      </button>
                      {planMenuOpen && (
                        <div id="archive-plan-options" className="archive-plan-options" aria-label="Plans de classement">
                          {groupedPlanOptions.map(({ plan, depth }) => (
                            <div
                              key={plan.id}
                              className={`archive-plan-option ${Number(plan.id) === Number(selectedPlanId) ? 'is-selected' : ''}`}
                              style={{ '--plan-depth': depth }}
                            >
                              {plan.children?.length > 0 ? (
                                <button type="button" className="archive-plan-branch" onClick={() => togglePlanBranch(plan.id)} aria-label={`${expandedPlanIds.has(Number(plan.id)) ? 'Réduire' : 'Développer'} ${plan.title}`} aria-expanded={expandedPlanIds.has(Number(plan.id))}>
                                  <i className={expandedPlanIds.has(Number(plan.id)) ? 'ri-subtract-line' : 'ri-add-line'} aria-hidden="true"></i>
                                </button>
                              ) : <span className="archive-plan-branch-spacer" aria-hidden="true"></span>}
                              <button type="button" className="archive-plan-option-select" aria-current={Number(plan.id) === Number(selectedPlanId) ? 'location' : undefined} title={`${plan.code} - ${plan.title}`} onClick={() => { setSelectedPlanId(plan.id); setPlanMenuOpen(false); }}>
                                <i className="ri-node-tree" aria-hidden="true"></i>
                                <span className="archive-plan-option-label"><strong>{plan.code}</strong><span>{plan.title}</span></span>
                                {Number(plan.id) === Number(selectedPlanId) && <i className="ri-check-line archive-plan-option-check" aria-hidden="true"></i>}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedPlanChildren.length > 0 && (
                    <div className="archive-plan-children">
                      <div className="archive-plan-children-list">
                        {selectedPlanChildren.map((plan) => (
                          <button
                            key={plan.id}
                            type="button"
                            className="archive-plan-chip"
                            onClick={() => setSelectedPlanId(plan.id)}
                            title={`${plan.code} - ${plan.title}`}
                          >
                            <i className="ri-node-tree" aria-hidden="true"></i><span>{plan.code} - {plan.title}</span><i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="archive-navigation-heading archive-navigation-heading-folders">Dossier</div>
                  <div className={`archive-tree ${!selectedPlanId ? 'disabled' : ''}`}>
                    <button type="button" className="archive-tree-up" onClick={goUp} disabled={!canGoUp} title={upLabel}>
                      <i className="ri-arrow-up-line" aria-hidden="true"></i><span>{upLabel}</span>
                    </button>
                    {selectedPlanId && <div className="archive-tree-current" title={['Racine', ...selectedPath.map((node) => node.title)].join(' › ')}><i className="ri-map-pin-2-line" aria-hidden="true"></i><span>{['Racine', ...selectedPath.map((node) => node.title)].join(' › ')}</span></div>}
                    {!selectedPlanId ? (
                      <div className="archive-empty">Sélectionnez un plan pour afficher les dossiers.</div>
                    ) : loading ? (
                      <div className="archive-loading">{SimpleSpinner()}</div>
                    ) : (
                      <ul>
                        {filteredTree.length > 0 ? (
                          filteredTree.map((node) => renderNode(node, filteredTree))
                        ) : (
                          <li>
                            <div className="archive-empty">Aucun dossier dans ce plan.</div>
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="archive-content">
                  <div className="archive-overview">
                  <div className="archive-context">
                    <div>
                      <div className="archive-context-title">{importBatchFilter ? 'Archives du lot ZIP' : planLabel}</div>
                      <div className="archive-breadcrumb">{importBatchFilter ? 'Tous les plans du lot' : (selectedPlan ? pathLabel : '—')}</div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-link archive-advanced-link"
                      onClick={handleAdvancedSearch}
                    >
                      Recherche avancée
                    </button>
                  </div>

                  <div className="archive-toolbar">
                    <div className="archive-search">
                      <select
                        className="form-select"
                        value={searchScope}
                        onChange={(e) => setSearchScope(e.target.value)}
                      >
                        <option value="all">Tous</option>
                        <option value="title">Titre</option>
                        <option value="profile">Profil</option>
                      </select>
                      <div className="input-group">
                        <input
                          className="form-control"
                          placeholder="Rechercher une archive"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <span className="input-group-text">
                          <i className="ri-search-line"></i>
                        </span>
                      </div>
                    </div>
                    <div className="archive-toolbar-actions">
                      {importBatchFilter && (
                        <div className="archive-import-batch-filter">
                          <span>Lot ZIP : {importBatchFilter}</span>
                          <button type="button" aria-label="Retirer le filtre du lot" onClick={() => navigate('/apps/in/archives')}>
                            <i className="ri-close-line" aria-hidden="true"></i>
                          </button>
                        </div>
                      )}
                      {importBatchFilter && !showTrash && canValidateDocuments && (
                        <button className="btn btn-primary btn-sm" type="button" onClick={() => { setValidateWholeBatch(true); setBatchModalOpen(true); }}>
                          Valider tout le lot ZIP
                        </button>
                      )}
                      {!showTrash && (
                        <select className="form-select form-select-sm" aria-label="Filtrer par validation" value={validationFilter} onChange={(event) => setValidationFilter(event.target.value)}>
                          <option value="">Toutes les validations</option>
                          <option value="draft">Brouillons</option>
                          <option value="En revue">En revue</option>
                          <option value="Actif Conservée">Validées</option>
                        </select>
                      )}
                      {canDeleteDocuments && (
                        <button
                          className={`btn btn-sm btn-uniform-action ${showTrash ? 'btn-danger' : 'btn-outline-danger'}`}
                          type="button"
                          onClick={() => setShowTrash((prev) => !prev)}
                        >
                          <i className="ri-delete-bin-6-line me-1"></i>
                          {showTrash ? 'Archives' : 'Corbeille'}
                        </button>
                      )}
                      <button
                        className="btn btn-success btn-sm btn-uniform-action"
                        type="button"
                        onClick={handleCreateArchive}
                        disabled={!canCreateArchive}
                      >
                        <i className="ri-add-circle-line me-1"></i> Nouvelle archive
                      </button>
                    </div>
                  </div>

                  </div>
                  <div className="archive-list">
                    {selectedIds.length > 0 && (
                      <div className="archive-selection-bar">
                        <div className="archive-selection-info">
                          <i className="ri-checkbox-circle-line"></i>
                          <span>
                            {selectedIds.length} archive{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="archive-selection-actions">
                          {showTrash ? (
                            <>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                type="button"
                                onClick={openRestoreModal}
                                disabled={!canDeleteDocuments}
                              >
                                <i className="ri-arrow-go-back-line me-1"></i> Restaurer
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                                type="button"
                                onClick={openDeleteModal}
                                disabled={!canDeleteDocuments}
                              >
                                <i className="ri-delete-bin-6-line me-1"></i> Supprimer définitivement
                              </button>
                            </>
                          ) : (
                            <>
                              {canValidateDocuments && (
                                <button className="btn btn-primary btn-sm" type="button" onClick={() => { setValidateWholeBatch(false); setBatchModalOpen(true); }}>
                                  <i className="ri-checkbox-circle-line me-1" aria-hidden="true"></i> Valider les imports ZIP
                                </button>
                              )}
                              <button
                                className="btn btn-outline-danger btn-sm"
                                type="button"
                                onClick={openTrashModal}
                                disabled={!canTrashDocuments}
                              >
                                <i className="ri-delete-bin-5-line me-1"></i> Envoyer en élimination
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-light btn-sm"
                            type="button"
                            onClick={() => setSelectedIds([])}
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}
                    {batchOutcome && (
                      <div className="alert alert-info m-3">
                        {batchOutcome.validated?.length || 0} archive(s) validée(s).
                        {(batchOutcome.skipped?.length || 0) > 0 && (
                          <details className="mt-2">
                            <summary>{batchOutcome.skipped.length} archive(s) ignorée(s) — voir les motifs</summary>
                            <ul className="mb-0 mt-2">
                              {batchOutcome.skipped.map((item) => <li key={item.id}>Archive n°{item.id} : {item.reason}</li>)}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                    {importBatchFilter && <button type="button" className="btn btn-sm mb-3" onClick={() => navigate(`/apps/in/archives/import?batchId=${encodeURIComponent(importBatchFilter)}`)}>Suivre les métadonnées de ce lot</button>}
                    <div className="archive-list-header">
                      <label className="archive-count">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleSelectAll}
                        />
                        <span>
                          {totalCount} {showTrash ? 'archives en corbeille' : 'archives'}
                        </span>
                      </label>
                      <div className="archive-list-actions">
                        <div className="archive-quick-filter">
                          <i className="ri-filter-2-line" aria-hidden="true"></i>
                          <input
                            type="search"
                            placeholder="Filtrer cette liste"
                            aria-label="Filtrer la liste des archives"
                            value={quickFilter}
                            onChange={(e) => setQuickFilter(e.target.value)}
                          />
                          {quickFilter && (
                            <button
                              type="button"
                              className="archive-filter-clear"
                              onClick={() => setQuickFilter('')}
                              aria-label="Effacer le filtre"
                            >
                              <i className="ri-close-line" aria-hidden="true"></i>
                            </button>
                          )}
                        </div>
                        <label className="archive-sort-control">
                          <span>Trier par</span>
                          <select
                            className="form-select form-select-sm"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                          >
                            <option value="date_desc">Plus récentes</option>
                            <option value="date_asc">Plus anciennes</option>
                            <option value="title">Titre A–Z</option>
                          </select>
                        </label>
                        <button
                          className="btn btn-outline-primary btn-sm archive-filter-reset"
                          type="button"
                          onClick={() => {
                            setQuickFilter('');
                            setSortBy('date_desc');
                          }}
                          disabled={!quickFilter && sortBy === 'date_desc'}
                        >
                          Réinitialiser
                        </button>
                      </div>
                    </div>

                    {docsLoading ? (
                      <div className="archive-loading">{SimpleSpinner()}</div>
                    ) : filteredDocs.length === 0 ? (
                      <div className="archive-empty">
                        {quickFilter.trim()
                          ? `Aucune archive ne correspond à « ${quickFilter.trim()} ».`
                          : searchTerm.trim()
                            ? `Aucune archive ne correspond à « ${searchTerm.trim()} ».`
                            : showTrash
                              ? 'Aucune archive dans la corbeille.'
                              : 'Aucune archive dans ce dossier.'}
                      </div>
                    ) : (
                      filteredDocs.map((doc) => (
                        <div className="archive-row" key={doc.id}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(doc.id)}
                            onChange={() => toggleSelectItem(doc.id)}
                          />
                          <div className="archive-row-icon">
                            <i className="ri-archive-line"></i>
                          </div>
                          <div className="archive-row-main">
                            <div className="archive-row-title">{doc.title}</div>
                            <div className="archive-row-meta">
                              {doc.profile?.title || doc.type || 'Profil'} · {doc.code} · {doc.date}
                              {doc.service?.title ? ` · ${doc.service.title}` : ''}
                            </div>
                          </div>
                          <span className={`badge-confidentialite ${confidentialityTone(doc.confidentialite)}`} aria-label={`Confidentialité : ${doc.confidentialite || 'non renseignée'}`}>
                            {doc.confidentialite || '—'}
                          </span>
                          {!showTrash && doc.validationStatus === 'draft' && (
                            <span className="archive-status-badge status-draft">Brouillon</span>
                          )}
                          {doc.status === 'En corbeille' && (
                            <span className="archive-status-badge status-trash">En corbeille</span>
                          )}
                          <div className="archive-row-actions">
                            {doc.status !== 'Détruite' && canViewDocuments && (
                              <button
                                className="btn btn-sm archive-row-view"
                                type="button"
                                title="Consulter l’archive"
                                aria-label={`Consulter l’archive ${doc.title}`}
                                onClick={() => handleView(doc)}
                              >
                                <i className="ri-eye-line" aria-hidden="true"></i>
                              </button>
                            )}
                            {doc.status !== 'Détruite' && (canViewDocuments || canDownloadDocuments || canEditDocuments) && (
                              <UncontrolledDropdown className={`archive-row-menu${!canDownloadDocuments && !canEditDocuments ? ' archive-row-menu-view-only' : ''}`}>
                                <DropdownToggle className="btn-sm archive-row-menu-toggle" title="Autres actions" aria-label={`Autres actions pour l’archive ${doc.title}`}>
                                  <i className="ri-more-2-fill" aria-hidden="true"></i>
                                </DropdownToggle>
                                <DropdownMenu end className="archive-row-dropdown">
                                  {canViewDocuments && (
                                    <DropdownItem className="archive-row-mobile-view" onClick={() => handleView(doc)}>
                                      <i className="ri-eye-line" aria-hidden="true" /> Consulter l’archive
                                    </DropdownItem>
                                  )}
                                  {canDownloadDocuments && (
                                    <DropdownItem
                                      onClick={() => handleDownload(doc)}
                                      disabled={Boolean(downloadUnavailableReason(doc))}
                                      title={downloadUnavailableReason(doc) || 'Télécharger l’archive'}
                                    >
                                      <i className="ri-download-2-line" aria-hidden="true" />
                                      {downloadUnavailableReason(doc) || 'Télécharger l’archive'}
                                    </DropdownItem>
                                  )}
                                  {canEditDocuments && (
                                    <DropdownItem
                                      onClick={() => handleEdit(doc)}
                                      disabled={doc.status === 'En corbeille'}
                                      title={doc.status === 'En corbeille' ? 'Édition indisponible : archive dans la corbeille' : 'Éditer l’archive'}
                                    >
                                      <i className="ri-pencil-line" aria-hidden="true" />
                                      {doc.status === 'En corbeille' ? 'Édition indisponible : archive dans la corbeille' : 'Éditer l’archive'}
                                    </DropdownItem>
                                  )}
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                    {!docsLoading && totalCount > 0 && (
                      <div className="archive-pagination">
                        <div className="archive-pagination-info">
                          Affichage {pageStart}-{pageEnd} sur {totalCount}
                        </div>
                        <div className="archive-pagination-controls">
                          <button
                            className="btn btn-light btn-sm"
                            type="button"
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            disabled={page <= 1}
                          >
                            Précédent
                          </button>
                          <span className="archive-pagination-page">
                            Page {page} / {totalPages}
                          </span>
                          <button
                            className="btn btn-light btn-sm"
                            type="button"
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={page >= totalPages}
                          >
                            Suivant
                          </button>
                          <select
                            className="form-select form-select-sm"
                            value={perPage}
                            onChange={(e) => setPerPage(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={batchModalOpen} toggle={() => setBatchModalOpen(false)} className="modal-close">
        <ModalHeader toggle={() => setBatchModalOpen(false)}>{validateWholeBatch ? 'Valider tout le lot ZIP' : 'Valider les imports ZIP sélectionnés'}</ModalHeader>
        <ModalBody>
          <p>{validateWholeBatch ? 'Toutes les archives en brouillon de ce lot seront contrôlées.' : `${selectedIds.length} archive(s) sélectionnée(s).`} Les archives issues d’un ZIP seront validées si leur fichier et leurs métadonnées obligatoires sont présents.</p>
          <p className="mb-0">Les autres seront ignorées avec un motif dans le rapport.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setBatchModalOpen(false)} disabled={actionLoading}>Annuler</Button>
          <Button color="primary" onClick={submitBatchValidation} disabled={actionLoading || (!validateWholeBatch && selectedIds.length === 0)}>
            {actionLoading ? 'Validation...' : 'Valider le lot'}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal isOpen={trashModalOpen} toggle={closeTrashModal} className="modal-close">
        <ModalHeader toggle={closeTrashModal}>
          Envoyer en élimination ({selectedIds.length} archive{selectedIds.length > 1 ? 's' : ''})
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Les archives seront déplacées vers la corbeille. La suppression définitive est réservée à l&apos;archiviste.
          </p>
          <div className="mb-3">
            <label className="form-label">Motif d&apos;élimination *</label>
            <select
              className="form-select"
              value={trashReason}
              onChange={(e) => setTrashReason(e.target.value)}
            >
              <option value="">Choisir un motif</option>
              {trashReasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">
              Commentaire {trashCommentRequired ? '*' : '(optionnel)'}
            </label>
            <textarea
              className="form-control"
              rows="3"
              value={trashComment}
              onChange={(e) => setTrashComment(e.target.value)}
              placeholder="Ex. Mauvais dossier ou doublon."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeTrashModal} disabled={actionLoading}>
            Annuler
          </Button>
          <Button color="danger" onClick={submitTrash} disabled={!canSubmitTrash || actionLoading}>
            Envoyer en élimination
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModalOpen} toggle={closeDeleteModal} className="modal-close">
        <ModalHeader toggle={closeDeleteModal}>
          Suppression définitive ({selectedIds.length} archive{selectedIds.length > 1 ? 's' : ''})
        </ModalHeader>
        <ModalBody>
          <div className="alert alert-danger">
            Cette action est irréversible. Les archives seront supprimées définitivement.
          </div>
          <div className="mb-3">
            <label className="form-label">Motif de suppression *</label>
            <select
              className="form-select"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
            >
              <option value="">Choisir un motif</option>
              {deleteReasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">
              Commentaire {deleteCommentRequired ? '*' : '(optionnel)'}
            </label>
            <textarea
              className="form-control"
              rows="3"
              value={deleteComment}
              onChange={(e) => setDeleteComment(e.target.value)}
              placeholder="Ex. Validation archiviste."
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Confirmer la suppression</label>
            <input
              className="form-control"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Tapez SUPPRIMER"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteModal} disabled={actionLoading}>
            Annuler
          </Button>
          <Button color="danger" onClick={submitDelete} disabled={!canSubmitDelete || actionLoading}>
            Supprimer définitivement
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={restoreModalOpen} toggle={closeRestoreModal} className="modal-close">
        <ModalHeader toggle={closeRestoreModal}>
          Restaurer ({selectedIds.length} archive{selectedIds.length > 1 ? 's' : ''})
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            Les archives seront restaurées dans la liste active.
          </p>
          <div className="mb-3">
            <label className="form-label">Motif de restauration *</label>
            <select
              className="form-select"
              value={restoreReason}
              onChange={(e) => setRestoreReason(e.target.value)}
            >
              <option value="">Choisir un motif</option>
              {restoreReasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">
              Commentaire {restoreCommentRequired ? '*' : '(optionnel)'}
            </label>
            <textarea
              className="form-control"
              rows="3"
              value={restoreComment}
              onChange={(e) => setRestoreComment(e.target.value)}
              placeholder="Ex. Restauration suite à une erreur."
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeRestoreModal} disabled={actionLoading}>
            Annuler
          </Button>
          <Button color="primary" onClick={submitRestore} disabled={!canSubmitRestore || actionLoading}>
            Restaurer
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Archives;
